import { __decorate } from "tslib";
import { Component, Mixins } from 'vue-property-decorator';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import { Mutation } from 'vuex-class';
import PageTitle from '@/components/v2/PageTitle.vue';
import appsQuery from '@/plugins/graphql/queries/app.gql';
import AppDelete from '@/plugins/graphql/mutations/app-delete.gql';
import WhiteLabelQuery from '@/plugins/graphql/queries/v2/white-label-content.gql';
import WhiteLabelRedirectMixin from '@/helpers/mixins/WhiteLabelRedirectMixin';
import ActionButton from '@/components/action_button/Index.vue';
import { AppModel } from '@/helpers/entities/models/AppModel';
import { AppStatus } from '@/helpers/entities/enums/v2/AppStatus';
import BaseOverlayLoader from '@/components/v2/BaseOverlayLoader';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import { ECOMMERCE_B2C, SURVEY, SHOPIFY, INQUIRY, STORE_MANAGEMENT, B2B, INSURANCE, DISTRIBUTOR, AFFILIATE, } from '@/helpers/constants/Bundles';
import { OVERVIEW_ROUTE as STORE_MANAGEMENT_OVERVIEW } from '@/helpers/constants/StoreManagementRoutes';
import { OVERVIEW_ROUTE as STORE_MANAGER_OVERVIEW } from '@/helpers/constants/StoreManagerRoutes';
import { OVERVIEW_ROUTE as INQUIRY_OVERVIEW } from '@/helpers/constants/InquiryRoutes';
import { OVERVIEW_ROUTE as B2B_OVERVIEW } from '@/helpers/constants/B2bRoutes';
import { STORE_MANAGEMENT as STORE_MANAGEMENT_KEY, MAIN_DISTRIBUTOR, } from '@/helpers/constants/CustomBundleKeys';
import { TierType } from '@/helpers/entities/enums/v2/TierType';
import PaginatorMixin from '@/helpers/mixins/PaginatorMixin';
import me from '@/plugins/graphql/queries/me.gql';
import { UserType } from '@/helpers/entities/enums/UserType';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import BaseSearchInput from '@/components/v2/BaseSearchInput';
import SearchMixin from '@/helpers/vue_mixins/v2/SearchMixin';
import { debounce, last } from 'lodash';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import Paginator from '@/components/v2/Paginator.vue';
let Apps = class Apps extends Mixins(Mixins(AlertMixin, WhiteLabelRedirectMixin, PaginatorMixin, SearchMixin, ViewportSizeMixin), CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.isRequestOnProcess = false;
        this.unfinishedDiyApp = false;
        this.apps = [];
        this.whiteLabelContent = null;
    }
    get pageTitle() {
        return this.$route.meta.title;
    }
    get isAffiliate() {
        const role = this.me?.role;
        if (!role) {
            return false;
        }
        return role === UserType.Affiliate || role === UserType.AffiliateManager;
    }
    get logoUrl() {
        if (this.whiteLabelContent) {
            return this.whiteLabelContent.whiteLabelFavicon;
        }
        return require('@/assets/images/cg_logo.png');
    }
    get appsPerPage() {
        return 12;
    }
    generateOverviewPathName(serviceBundleName, isTenant, isDistributor) {
        // TODO: Dynamic bundle mapping for overview page
        switch (serviceBundleName) {
            case ECOMMERCE_B2C:
                return 'ecommerce-overview';
            case SURVEY:
                return 'survey-overview';
            case SHOPIFY:
                return 'shopify-overview';
            case INQUIRY:
                return INQUIRY_OVERVIEW.name;
            case B2B:
                return B2B_OVERVIEW.name;
            case STORE_MANAGEMENT:
                return isTenant ? STORE_MANAGER_OVERVIEW.name : STORE_MANAGEMENT_OVERVIEW.name;
            case INSURANCE:
                return 'insurance-overview';
            case DISTRIBUTOR:
                return isDistributor ? 'distributor-overview' : 'main-distributor-overview';
            case AFFILIATE:
                return 'affiliate-overview';
            default:
                return 'apps-dashboard';
        }
    }
    generateOverviewPathKey(app) {
        if (app.bundle.name === DISTRIBUTOR && !app.isDistributor) {
            return MAIN_DISTRIBUTOR;
        }
        if (app.isTenant) {
            return STORE_MANAGEMENT_KEY;
        }
        return app.bundle.key;
    }
    success(status) {
        return status === AppStatus.approved;
    }
    error(app) {
        return this.displayStatus(app) === 'Deactivated' || this.displayStatus(app) === 'Disconnected';
    }
    declined(status) {
        return status === AppStatus.declined;
    }
    approved(status) {
        return status === AppStatus.approved;
    }
    warning(app) {
        return app.request.status === AppStatus.pending || app.maintenance;
    }
    toggleConfirmation(uuid, isUnfinishedDiyApp) {
        this.selectedAppUuid = uuid;
        this.unfinishedDiyApp = isUnfinishedDiyApp;
        this.showConfirmationDialog();
    }
    async uponConfirm() {
        this.isRequestOnProcess = !this.isRequestOnProcess;
        await this.deleteMiniApp()
            .then(() => {
            const appType = this.unfinishedDiyApp ? 'Draft Mini App' : 'In-App Store';
            this.successNotification(`Successfully Deleted ${appType}`);
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.isRequestOnProcess = false;
            this.hideConfirmationDialog();
        });
    }
    async deleteMiniApp() {
        await this.$apollo.mutate({
            mutation: AppDelete,
            variables: {
                input: {
                    appUuid: this.selectedAppUuid,
                },
            },
            // need to fix
            // update: (cache: any) => {
            //   const queryOptions = {
            //     query: appsQuery,
            //   };
            //   const { apps } = cache.readQuery({
            //     ...queryOptions,
            //   });
            //   const deletedApp = (app: { node: AppPayload }) => {
            //     return app.node.uuid !== this.selectedAppUuid;
            //   };
            //   cache.writeQuery({
            //     ...queryOptions,
            //     data: {
            //       apps: {
            //         ...apps,
            //         edges: apps.edges.filter(deletedApp),
            //       },
            //     },
            //   });
            // },
            refetchQueries: [
                {
                    query: appsQuery,
                    variables: {
                        first: this.appsPerPage,
                        after: this.startCursor || null,
                        keyword: this.search
                    },
                },
            ],
        });
    }
    displayStatus(app) {
        const { request: { status }, active, deactivated, launchedAt, storeStatus, maintenance, activePage, } = app;
        if (status === AppStatus.approved) {
            if ((active && !app.maintenance) || storeStatus === 'active_store') {
                return 'Active';
            }
            if (deactivated) {
                return 'Deactivated';
            }
            if (activePage === null) {
                return 'Disconnected';
            }
            if (launchedAt === null && storeStatus === null && !active) {
                return 'Unlaunched';
            }
            if (maintenance) {
                return 'Under Maintenance';
            }
        }
        return status;
    }
    get buildDiyLink() {
        // Initial commit remove this after
        const plan = this.$router.currentRoute.query.plan_type;
        const code = this.$router.currentRoute.query.affiliate_code;
        if (plan === TierType.ProStarter) {
            return `/diy/choose-service-bundle?plan_type=${TierType.ProStarter}${this.addCodeParameter(code)}`;
        }
        if (plan === TierType.ProBronze) {
            return `/diy/choose-service-bundle?plan_type=${TierType.ProBronze}${this.addCodeParameter(code)}`;
        }
        if (plan === TierType.ProSilver) {
            return `/diy/choose-service-bundle?plan_type=${TierType.ProSilver}${this.addCodeParameter(code)}`;
        }
        if (!plan && code) {
            return `/diy/choose-service-bundle?affiliate_code=${code}`;
        }
        return '/diy/choose-service-bundle';
    }
    addCodeParameter(code) {
        if (code) {
            return `&affiliate_code=${code}`;
        }
        return '';
    }
    get dialogMessage() {
        return this.unfinishedDiyApp
            ? 'Remove In-App Store?'
            : 'Deleting your in-app store is permanent. You will lose all features, integrations, and notifications.';
    }
    get dialogHeader() {
        return this.unfinishedDiyApp ? undefined : 'Are You Sure?';
    }
    get dialogConfirmLabel() {
        return this.unfinishedDiyApp ? undefined : 'Delete Store';
    }
    mounted() {
        // removal of pagination cursor when switching app
        Object.keys(localStorage)
            .filter((x) => x.endsWith('_cursor'))
            .forEach((x) => localStorage.removeItem(x));
        this.viewAppsPage();
        // remove any persistent error message alert
        this.$store.commit('toggleAlert', false);
        this.$store.commit('clearAlertMessage');
    }
};
__decorate([
    Mutation('showConfirmationDialog')
], Apps.prototype, "showConfirmationDialog", void 0);
__decorate([
    Mutation('hideConfirmationDialog')
], Apps.prototype, "hideConfirmationDialog", void 0);
Apps = __decorate([
    Component({
        components: {
            ActionButton,
            PageTitle,
            BaseOverlayLoader,
            ConfirmationDialog,
            BaseSearchInput,
            Paginator,
        },
        apollo: {
            apps: {
                query: appsQuery,
                variables() {
                    return { first: this.appsPerPage, after: this.startCursor || null, keyword: this.search };
                },
                update(data) {
                    const apps = data.apps.edges;
                    this.pageInfo = data.apps.pageInfo;
                    this.totalCount = data.apps.totalCount;
                    return apps.map((item) => {
                        return new AppModel(item.node);
                    });
                },
            },
            me: {
                query: me,
                skip() {
                    return !this.$store.state.session.token;
                },
            },
            whiteLabelContent: {
                query: WhiteLabelQuery,
                variables() {
                    return {
                        origin: this.getOrigin,
                    };
                },
                update(data) {
                    if (data.whiteLabelContent) {
                        return data.whiteLabelContent;
                    }
                    return null;
                },
            },
        },
        methods: {
            waitForQueryToFinish(searchInput) {
                return new Promise((resolve) => {
                    const checkLoading = () => {
                        if (this.$apollo.queries.apps.loading) {
                            if (searchInput) {
                                const inputElement = searchInput;
                                inputElement.blur();
                            }
                            setTimeout(checkLoading, 200);
                        }
                        else {
                            resolve();
                        }
                    };
                    checkLoading();
                });
            },
            // eslint-disable-next-line
            debounceSearch: debounce(async function (event) {
                const searchInput = document.querySelector('#apps-search-bar input');
                const cursorName = `${this.pageTitle.toLowerCase()}_cursor`;
                const cursorStorage = localStorage.getItem(cursorName);
                if (cursorStorage) {
                    this.startCursor = last(JSON.parse(cursorStorage));
                }
                await this.onSearch(event);
                this.waitForQueryToFinish(searchInput).then(() => {
                    if (searchInput) {
                        const inputElement = searchInput;
                        inputElement.focus();
                    }
                });
            }, 900),
        },
        beforeCreate() {
            this.$store.commit('navigation_drawer/toggleDrawer', false);
        },
    })
], Apps);
export default Apps;
